import type { ReactNode } from 'react';
import { BookOpen, Check, CircleHelp, Equal, Info, Lightbulb, Microscope, X } from 'lucide-react';
import EducationalVisual from '@/components/feature/EducationalVisual';

interface FormattedMessageProps {
  content: string;
  isUser: boolean;
  theme: 'dark' | 'light';
}

type EducationalType = 'goal' | 'def' | 'concept' | 'formula' | 'note' | 'science' | 'calc' | 'why' | 'misconception' | 'truth' | 'example' | 'working' | 'important' | 'takeaway';
type Block = { type: 'heading' | 'subheading' | 'paragraph' | 'divider' | 'code' | 'educational' | 'numbered_list' | 'bullet_list' | 'table'; content: string; language?: string; eduType?: EducationalType; items?: { prefix: string; text: string }[]; headers?: string[]; rows?: string[][]; alignments?: ('left' | 'center' | 'right')[] };
const educationalTag = /^(GOAL|DEF|CONCEPT|FORMULA|NOTE|SCIENCE|CALC|WHY|MISCONCEPTION|TRUTH|EXAMPLE|WORKING|IMPORTANT|TAKEAWAY):\s*(.*)$/i;
const config = {
  goal: { label: 'Learning goal', icon: Lightbulb },
  def: { label: 'Definition', icon: BookOpen },
  concept: { label: 'Key concept', icon: Lightbulb },
  formula: { label: 'Core formula', icon: Equal },
  note: { label: 'Important note', icon: Info },
  science: { label: 'How it works', icon: Microscope },
  calc: { label: 'Calculation', icon: Equal },
  why: { label: 'Why this matters', icon: CircleHelp },
  misconception: { label: 'Common misconception', icon: X },
  truth: { label: 'What’s actually true', icon: Check },
  example: { label: 'Example', icon: BookOpen },
  working: { label: 'Working', icon: Equal },
  important: { label: 'Important note', icon: Info },
  takeaway: { label: 'Takeaway', icon: Check },
};

function parseInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /(\*\*(.+?)\*\*|`([^`]+)`|\*([^*\n]+)\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(match[2] ? <strong key={match.index} className="font-semibold text-inherit">{match[2]}</strong> : match[3] ? <code key={match.index} className="rounded bg-neutral-500/10 px-1.5 py-0.5 text-[.9em]">{match[3]}</code> : <em key={match.index}>{match[4]}</em>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

function isBoundary(line: string) {
  return educationalTag.test(line) || /^(#{1,6}\s|```|[-*]\s|\d+[.)]\s|>\s)/.test(line) || line.includes('|') || /^([-_*])\1{2,}$/.test(line) || /^\*\*[^*]+\*\*$/.test(line) || /^\*[^*].*[^*]\*$/.test(line);
}

function tableCells(line: string): string[] {
  let value = line.trim();
  if (value.startsWith('|')) value = value.slice(1);
  if (value.endsWith('|') && !value.endsWith('\\|')) value = value.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  let inCode = false;
  for (let i = 0; i < value.length; i++) {
    if (value[i] === '\\' && value[i + 1] === '|') { cell += '|'; i++; }
    else if (value[i] === '`') { inCode = !inCode; cell += value[i]; }
    else if (value[i] === '|' && !inCode) { cells.push(cell.trim()); cell = ''; }
    else cell += value[i];
  }
  cells.push(cell.trim());
  return cells;
}

/* Accept legacy tagged lessons, plain prose, Markdown, and streamed unclosed fences.
 * Never let a list consume a later heading, educational card, or visual. */
function parseContent(raw: string): Block[] {
  const lines = raw.replace(/\r\n/g, '\n').split('\n').map(line => {
    const trimmed = line.trim();
    const unbolded = trimmed.replace(/^\*\*([^*]+)\*\*(.*)$/, '$1$2');
    return educationalTag.test(unbolded) ? unbolded : line;
  });
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) { i++; continue; }
    if (trimmed.includes('|') && i + 1 < lines.length) {
      const headers = tableCells(trimmed);
      const separators = tableCells(lines[i + 1]);
      if (headers.length > 1 && separators.length === headers.length && separators.every(cell => /^:?-{3,}:?$/.test(cell))) {
        const alignments = separators.map(cell => cell.startsWith(':') && cell.endsWith(':') ? 'center' as const : cell.endsWith(':') ? 'right' as const : 'left' as const);
        const rows: string[][] = [];
        i += 2;
        while (i < lines.length && lines[i].trim().includes('|') && !/^(#{1,6}\s|```)/.test(lines[i].trim())) {
          const cells = tableCells(lines[i++]);
          rows.push(headers.map((_, index) => cells[index] || ''));
        }
        blocks.push({ type: 'table', content: '', headers, rows, alignments });
        continue;
      }
    }
    if (/^([-_*])\1{2,}$/.test(trimmed)) { blocks.push({ type: 'divider', content: '' }); i++; continue; }
    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim().toLowerCase();
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) code.push(lines[i++]);
      if (i < lines.length) i++;
      blocks.push({ type: 'code', content: code.join('\n'), language });
      continue;
    }
    if (/^>\s?/.test(trimmed)) {
      const note: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) note.push(lines[i++].trim().replace(/^>\s?/, ''));
      blocks.push({ type: 'educational', eduType: 'note', content: note.join(' ') });
      continue;
    }
    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: heading[1].length === 1 ? 'heading' : 'subheading', content: heading[2].replace(/\s+#+$/, '') });
      i++; continue;
    }
    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      if (trimmed.includes('=') && trimmed.length < 140) {
        blocks.push({ type: 'educational', eduType: 'formula', content: trimmed.slice(2, -2) }); i++; continue;
      }
      blocks.push({ type: 'heading', content: trimmed.slice(2, -2) }); i++; continue;
    }
    if (/^\*[^*].*[^*]\*$/.test(trimmed) && !/^\*\s/.test(trimmed)) {
      blocks.push({ type: 'subheading', content: trimmed.slice(1, -1) }); i++; continue;
    }
    const educational = trimmed.match(educationalTag);
    if (educational) {
      const content = [educational[2]];
      i++;
      while (i < lines.length && lines[i].trim() && !isBoundary(lines[i].trim())) content.push(lines[i++].trim());
      blocks.push({ type: 'educational', eduType: educational[1].toLowerCase() as EducationalType, content: content.join(/^(formula|calc|working)$/i.test(educational[1]) ? '\n' : ' ') });
      continue;
    }
    const numbered = /^\d+[.)]\s/.test(trimmed);
    const bullet = /^[-*]\s/.test(trimmed);
    if (numbered || bullet) {
      const items: { prefix: string; text: string }[] = [];
      const pattern = numbered ? /^(\d+)[.)]\s+(.*)$/ : /^([-*])\s+(.*)$/;
      while (i < lines.length) {
        const next = lines[i].trim();
        if (!next) { i++; break; }
        const item = next.match(pattern);
        if (item) { items.push({ prefix: item[1], text: item[2] }); i++; }
        else if (!isBoundary(next) && items.length) { items[items.length - 1].text += ` ${next}`; i++; }
        else break;
      }
      blocks.push({ type: numbered ? 'numbered_list' : 'bullet_list', content: '', items });
      continue;
    }
    const paragraph = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !isBoundary(lines[i].trim())) paragraph.push(lines[i++].trim());
    blocks.push({ type: 'paragraph', content: paragraph.join(' ') });
  }
  return blocks;
}

export default function FormattedMessage({ content, isUser, theme }: FormattedMessageProps) {
  if (isUser) return <p className="text-[15px] leading-relaxed font-medium whitespace-pre-wrap break-words">{parseInline(content)}</p>;
  const textColor = theme === 'dark' ? 'text-[#c9c9ce]' : 'text-neutral-700';
  return (
    <div className={`formatted-lesson ${textColor}`}>
      {parseContent(content).map((block, index) => {
        const inline = parseInline(block.content);
        switch (block.type) {
          case 'heading': return <h3 key={index} className={theme === 'dark' ? 'text-[#efeff0]' : 'text-neutral-900'}>{inline}</h3>;
          case 'subheading': return <h4 key={index} className="font-semibold text-inherit">{inline}</h4>;
          case 'paragraph': return <p key={index}>{inline}</p>;
          case 'divider': return <hr key={index} className="border-neutral-500/20 my-2" />;
          case 'table': return <div key={index} className="lesson-table-scroll" role="region" aria-label="Study table — scroll horizontally if needed" tabIndex={0}>
            <table className="lesson-table">
              <thead><tr>{block.headers?.map((cell, idx) => <th key={idx} scope="col" style={{ textAlign: block.alignments?.[idx] }}>{parseInline(cell)}</th>)}</tr></thead>
              <tbody>{block.rows?.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, idx) => <td key={idx} style={{ textAlign: block.alignments?.[idx], fontVariantNumeric: 'tabular-nums' }}>{parseInline(cell)}</td>)}</tr>)}</tbody>
            </table>
          </div>;
          case 'code': return block.language === 'lavyn-visual'
            ? <EducationalVisual key={index} source={block.content} />
            : <pre key={index} className="lesson-code" aria-label={block.language ? `${block.language} example` : 'Worked example'}><code>{block.content}</code></pre>;
          case 'educational': {
            const eduType = block.eduType!;
            if (eduType === 'goal') return <p key={index} className="font-semibold text-inherit">{inline}</p>;
            const { label, icon: Icon } = config[eduType];
            return <section key={index} className="educational-card" data-kind={eduType} data-theme={theme}><header><Icon size={17} aria-hidden="true" /><span>{label}</span></header><p>{inline}</p></section>;
          }
          case 'numbered_list': return <ol key={index} className="flex flex-col gap-4">{block.items?.map((item, idx) => <li key={idx} className="flex gap-3.5 items-start"><span className="flex-shrink-0 w-7 h-7 mt-0.5 rounded-full border border-neutral-500/20 bg-neutral-500/10 grid place-items-center text-xs font-semibold">{item.prefix}</span><p className="min-w-0 flex-1">{parseInline(item.text)}</p></li>)}</ol>;
          case 'bullet_list': return <ul key={index} className="list-disc pl-5 flex flex-col gap-3 marker:text-neutral-500">{block.items?.map((item, idx) => <li key={idx} className="pl-1 leading-[1.85]">{parseInline(item.text)}</li>)}</ul>;
          default: return null;
        }
      })}
    </div>
  );
}